import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useCurrency } from '@/contexts/CurrencyContext';
import { CURRENCY_COUNTRIES } from '@/lib/currencyCountries';

function flagUrl(iso2: string) {
  return `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`;
}

export function CountryCurrencySelect({
  className = '',
  variant = 'header',
}: {
  className?: string;
  /** `header` = on primary nav bar; `panel` = on light card (e.g. mobile menu). */
  variant?: 'header' | 'panel';
}) {
  const { selectedCountry, setSelectedCountryIso2, ratesLoading } = useCurrency();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CURRENCY_COUNTRIES;
    return CURRENCY_COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        c.currency.toLowerCase().includes(q)
    );
  }, [query]);

  const btn =
    variant === 'header'
      ? 'border-primary-foreground/25 bg-primary-foreground/5 hover:bg-primary-foreground/10 text-primary-foreground'
      : 'border-border bg-muted/60 hover:bg-muted text-foreground';

  return (
    <div className={`relative ${className}`} ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 pl-2 pr-2 py-1.5 rounded-xl border transition-colors text-sm w-full sm:w-auto justify-between ${btn}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Select country for currency"
      >
        <img
          src={flagUrl(selectedCountry.iso2)}
          alt=""
          width={22}
          height={16}
          className={`rounded-sm object-cover shrink-0 border ${
            variant === 'header' ? 'border-primary-foreground/10' : 'border-border'
          }`}
        />
        <span className="hidden sm:inline max-w-[100px] truncate text-xs font-medium">
          {selectedCountry.iso2}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 opacity-90 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-[60] w-[min(100vw-1.5rem,320px)] rounded-2xl border border-border bg-card text-foreground shadow-xl overflow-hidden flex flex-col max-h-[min(70vh,380px)]">
          <div className="p-2 border-b border-border bg-muted/30">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or currency…"
                className="pl-9 h-10 rounded-xl bg-background"
                autoFocus
              />
            </div>
            {ratesLoading && (
              <p className="text-[10px] text-muted-foreground mt-1.5 px-1">Loading exchange rates…</p>
            )}
          </div>
          <ul className="overflow-y-auto py-1" role="listbox">
            {filtered.map((c) => {
              const active = c.iso2 === selectedCountry.iso2;
              return (
                <li key={c.iso2} role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-muted transition-colors ${
                      active ? 'bg-muted/80' : ''
                    }`}
                    onClick={() => {
                      setSelectedCountryIso2(c.iso2);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    <img
                      src={flagUrl(c.iso2)}
                      alt=""
                      width={28}
                      height={20}
                      className="rounded object-cover border border-border shrink-0"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="font-medium block truncate">{c.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {c.currency} · {c.iso2}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="px-3 py-6 text-sm text-muted-foreground text-center">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
